import type { FontFamily } from './fonts';

export type Theme = 'light' | 'dark' | 'os';

export interface TransitionConfig {
  duration: {
    in: number;
    out: number;
  };
  translate: {
    in: string;
    out: string;
  };
  opacity: {
    start: number;
    end: number;
  };
  easing: {
    in: string;
    out: string;
  };
  navigationDelay: number;
}

export interface ThemeConfig {
  fontFamily: FontFamily;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor?: string;
  textOnPrimary: string;
  textOnSecondary: string;
  textOnTertiary?: string;
  bgColor: string;
  bgColorDark: string;
  theme: Theme;
  radius: string;
  disabledOpacity: string;
  transition: string;
  successColor: string;
  warningColor: string;
  dangerColor: string;
  textOnSuccess: string;
  textOnWarning: string;
  textOnDanger: string;
  textColor: string;
  textColorLight: string;
  textColorDark: string;
  borderColor: string;
  borderColorDark: string;
  delay?: number;
  transitions: TransitionConfig;
}

// Default theme configuration
export const defaultTheme: ThemeConfig = {
  fontFamily: 'Avenir*',
  primaryColor: '#d9188e',
  secondaryColor: '#614d99',
  tertiaryColor: '#5B77CE',
  textOnPrimary: '#ffffff',
  textOnSecondary: '#ffffff',
  textOnTertiary: '#ffffff',
  bgColor: '#ffffff',
  bgColorDark: '#1a1a1a',
  theme: 'light',
  radius: '0.5rem',
  disabledOpacity: '0.5',
  transition: '150ms ease-in-out',
  successColor: '#22c55e',
  warningColor: '#f59e0b',
  dangerColor: '#ef4444',
  textOnSuccess: '#ffffff',
  textOnWarning: '#ffffff',
  textOnDanger: '#ffffff',
  textColor: '#1f2937',
  textColorLight: '#f3f4f6',
  textColorDark: '#f3f4f6',
  borderColor: '#e5e7eb',
  borderColorDark: '#374151',
  delay: 0,
  transitions: {
    duration: {
      in: 400,
      out: 300
    },
    translate: {
      in: "35%",
      out: "5%"
    },
    opacity: {
      start: 0,
      end: 0
    },
    easing: {
      in: "cubic-bezier(0.33, 1, 0.68, 1)", // cubicOut
      out: "cubic-bezier(0.32, 0, 0.67, 0)", // cubicIn
    },
    navigationDelay: 201
  }
};

// Function to merge custom config with defaults
export function getThemeConfig(customConfig?: Partial<ThemeConfig>): ThemeConfig {
  const config = {
    ...defaultTheme,
    ...customConfig,
    // Deep merge transitions if provided
    transitions: {
      ...defaultTheme.transitions,
      ...(customConfig?.transitions || {}),
    }
  };

  return config;
}

// Example of how the JSON from backend might look
export const exampleConfigFromBackend = {
  primaryColor: '#d9188e',
  theme: 'dark',
};
